import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CommunicationDeliveryMode,
  CrmBranchView,
  CrmDrilldownKind,
  CrmReportQuery,
  CustomerCommunicationStatus,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Card } from '@/components/ui/Card';
import { useLocale } from '@/hooks/useLocale';
import { CommunicationStatusBadge } from '@/modules/crm/components/communicationLabels';
import { useCrmRecordsQuery } from '../api/reports.api';
import { ExportCsvButton } from './ExportCsvButton';
import { failureReasonLabel } from './crmLabels';

const PAGE_SIZE = 20;

export interface CrmRecordsRequest {
  kind: CrmDrilldownKind;
  bucketStatus?: CustomerCommunicationStatus;
  bucketMode?: CommunicationDeliveryMode;
  bucketReason?: string;
  branchView?: CrmBranchView;
  /** What the figure is called, already translated. */
  title: string;
}

/**
 * The records behind one CRM figure, paged by the server under the report's own scope and filters,
 * so the count shown is the figure itself. A communication shows only what an operator needs to find
 * it — never its words, a phone number or anything the provider returned.
 */
export function CrmRecords({
  query,
  request,
  onClose,
}: {
  query: CrmReportQuery;
  request: CrmRecordsRequest;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [page, setPage] = useState(1);
  const recordsQuery = {
    ...query,
    compare: undefined,
    kind: request.kind,
    bucketStatus: request.bucketStatus,
    bucketMode: request.bucketMode,
    bucketReason: request.bucketReason,
    branchView: request.branchView,
    page,
    pageSize: PAGE_SIZE,
  };
  const records = useCrmRecordsQuery(recordsQuery);
  const data = records.data;
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const headingId = `crm-records-${request.kind}-${request.bucketStatus ?? request.bucketMode ?? request.bucketReason ?? request.branchView ?? 'all'}`;
  const branchKind = request.kind === 'WHATSAPP_BRANCHES' || request.kind === 'AUTOMATION_BRANCHES';
  const ltr = (value: ReactNode) => <span dir="ltr">{value}</span>;
  const cell = 'py-2';
  const head = (labels: string[]) => (
    <tr className="text-muted border-line border-b">
      {labels.map((label) => (
        <th key={label} className="py-2 text-start font-medium">
          {label}
        </th>
      ))}
    </tr>
  );

  let table: ReactNode = null;
  if (data && request.kind === 'WHATSAPP_BRANCHES' && data.whatsappBranches.length > 0) {
    table = (
      <>
        <thead>
          {head([
            t('reports.crm.whatsapp.columns.branch'),
            t('reports.crm.whatsapp.columns.automation'),
            t('reports.crm.whatsapp.columns.runtime'),
            t('reports.crm.whatsapp.columns.canSend'),
          ])}
        </thead>
        <tbody>
          {data.whatsappBranches.map((row) => (
            <tr key={row.branchId} className="border-line border-b last:border-b-0">
              <td className={cell}>{row.branchName}</td>
              <td className={cell}>
                {row.automationEnabled
                  ? t('reports.crm.whatsapp.on')
                  : t('reports.crm.whatsapp.off')}
              </td>
              <td className={cell}>
                {row.runtimeStatus
                  ? t(`crm.whatsappStatus.${row.runtimeStatus}`)
                  : t('reports.crm.whatsapp.noSession')}
              </td>
              <td className={cell}>{row.canSend ? t('reports.crm.yes') : t('reports.crm.no')}</td>
            </tr>
          ))}
        </tbody>
      </>
    );
  } else if (data && request.kind === 'AUTOMATION_BRANCHES' && data.automationBranches.length > 0) {
    table = (
      <>
        <thead>
          {head([
            t('reports.crm.automation.columns.branch'),
            t('reports.crm.automation.columns.source'),
            t('reports.crm.automation.columns.state'),
            t('reports.crm.automation.columns.template'),
          ])}
        </thead>
        <tbody>
          {data.automationBranches.map((row) => (
            <tr key={row.branchId} className="border-line border-b last:border-b-0">
              <td className={cell}>{row.branchName}</td>
              <td className={cell}>{t(`reports.crm.automation.source.${row.source}`)}</td>
              <td className={cell}>
                {row.enabled ? t('reports.crm.automation.on') : t('reports.crm.automation.off')}
              </td>
              <td className={cell}>
                {(locale === 'ar' && row.templateNameAr) ||
                  row.templateName ||
                  t('reports.crm.none')}
              </td>
            </tr>
          ))}
        </tbody>
      </>
    );
  } else if (data && data.communications.length > 0) {
    table = (
      <>
        <thead>
          {head([
            t('reports.crm.records.date'),
            t('reports.crm.records.branch'),
            t('reports.crm.records.customer'),
            t('reports.crm.records.purpose'),
            t('reports.crm.records.mode'),
            t('reports.crm.records.status'),
            t('reports.crm.records.template'),
            t('reports.crm.records.reason'),
            t('reports.crm.records.attempts'),
          ])}
        </thead>
        <tbody>
          {data.communications.map((row) => (
            <tr key={row.id} className="border-line border-b last:border-b-0">
              <td className={cell}>{ltr(row.businessDate)}</td>
              <td className={cell}>{row.branchName}</td>
              <td className={cell}>{row.customerName ?? t('reports.crm.none')}</td>
              <td className={cell}>{t(`crm.purpose.${row.purpose}`)}</td>
              <td className={cell}>{t(`reports.crm.mode.${row.deliveryMode}`)}</td>
              <td className={cell}>
                <CommunicationStatusBadge status={row.status} />
              </td>
              <td className={cell}>
                {(locale === 'ar' && row.templateNameAr) ||
                  row.templateName ||
                  t('reports.crm.volume.customText')}
              </td>
              <td className={cell}>
                {row.suppressionReason
                  ? t(`reports.crm.suppressionReason.${row.suppressionReason}`)
                  : row.status === 'FAILED'
                    ? failureReasonLabel(t, row.failureCode ?? 'UNRECORDED')
                    : t('reports.crm.none')}
              </td>
              <td className={`${cell} text-end tabular-nums`}>{ltr(row.attempts)}</td>
            </tr>
          ))}
        </tbody>
      </>
    );
  }

  return (
    <Card className="min-w-0 p-4">
      <section aria-labelledby={headingId}>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h3 id={headingId} className="text-ink font-semibold">
            {request.title}
          </h3>
          <span className="flex flex-wrap items-baseline gap-3">
            <ExportCsvButton
              target={{ method: 'GET', path: '/reports/crm/records/export', query: recordsQuery }}
            />
            <button
              type="button"
              onClick={onClose}
              className="text-brand-700 text-sm font-medium hover:underline"
            >
              {t('reports.sales.records.close')}
            </button>
          </span>
        </div>
        {records.isError ? (
          <div className="mt-3">
            <Alert tone="error">{t('reports.sales.records.loadError')}</Alert>
          </div>
        ) : null}
        {records.isPending ? (
          <div
            className="bg-surface border-line mt-3 h-24 animate-pulse rounded-lg border"
            aria-hidden
          />
        ) : null}
        {data ? (
          <p className="text-muted mt-2 text-sm">
            {branchKind
              ? t('reports.crm.records.branchCount', { count: data.total })
              : t('reports.crm.records.count', { count: data.total })}
          </p>
        ) : null}
        {data && data.total === 0 ? (
          <p className="text-muted mt-3 text-sm">{t('reports.sales.records.empty')}</p>
        ) : null}
        {table ? (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[40rem] text-sm [&_td]:px-2 [&_th]:px-2">{table}</table>
          </div>
        ) : null}
        {data && pages > 1 ? (
          <div className="mt-3 flex items-center justify-between gap-3 text-sm">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
              className="text-brand-700 font-medium hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('reports.sales.records.previous')}
            </button>
            <span className="text-muted">{t('reports.sales.records.page', { page, pages })}</span>
            <button
              type="button"
              disabled={page >= pages}
              onClick={() => setPage((current) => current + 1)}
              className="text-brand-700 font-medium hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('reports.sales.records.next')}
            </button>
          </div>
        ) : null}
      </section>
    </Card>
  );
}
